import { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Turtle,
  Scale,
  Rocket,
  Coins,
  Globe,
  HelpCircle,
  AlertTriangle,
  BookOpen,
  Plus,
  Search,
  X,
  Eye,
  Target,
  Shield,
  Brain,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASKET_ICONS = {
  steady_eddie: "🐢",
  balanced_builder: "⚖️",
  growth_seeker: "🚀",
  income_hunter: "💷",
  global_explorer: "🌍"
};

const BASKET_COLORS = {
  green: "border-green-500/50 bg-green-500/5 hover:bg-green-500/10",
  blue: "border-blue-500/50 bg-blue-500/5 hover:bg-blue-500/10",
  red: "border-red-500/50 bg-red-500/5 hover:bg-red-500/10",
  amber: "border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10",
  purple: "border-purple-500/50 bg-purple-500/5 hover:bg-purple-500/10"
};

export const PortfolioWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null); // 'guided', 'custom', 'hybrid'
  const [investment, setInvestment] = useState(1000);
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [customFunds, setCustomFunds] = useState([]);
  const [customStocks, setCustomStocks] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guide, setGuide] = useState([]);

  useEffect(() => {
    fetchBaskets();
    fetchQuestions();
    fetchGuide();
  }, []);

  const fetchBaskets = async () => {
    try {
      const response = await axios.get(`${API}/wizard/baskets`);
      setBaskets(response.data.baskets || []);
    } catch (error) {
      console.error("Error fetching baskets:", error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/wizard/questions`);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const fetchGuide = async () => {
    try {
      const response = await axios.get(`${API}/guide`);
      setGuide(response.data.sections || []);
    } catch (error) {
      console.error("Error fetching guide:", error);
    }
  };

  const searchFunds = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/wizard/fund-search?query=${query}`);
      setSearchResults(response.data.funds || []);
    } catch (error) {
      console.error("Error searching funds:", error);
    } finally {
      setSearching(false);
    }
  };

  const addCustomFund = (fund) => {
    if (!customFunds.find(f => f.id === fund.id)) {
      setCustomFunds([...customFunds, { ...fund, allocation: 100 / (customFunds.length + 1) }]);
      // Rebalance allocations
      const newFunds = [...customFunds, fund];
      const equalAllocation = 100 / newFunds.length;
      setCustomFunds(newFunds.map(f => ({ ...f, allocation: equalAllocation })));
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeCustomFund = (fundId) => {
    const newFunds = customFunds.filter(f => f.id !== fundId);
    if (newFunds.length > 0) {
      const equalAllocation = 100 / newFunds.length;
      setCustomFunds(newFunds.map(f => ({ ...f, allocation: equalAllocation })));
    } else {
      setCustomFunds([]);
    }
  };

  const analyzePortfolio = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/wizard/analyze`, {
        investment_amount: investment,
        basket_id: selectedBasket?.id,
        custom_funds: customFunds.length > 0 ? customFunds : null,
        custom_stocks: customStocks.length > 0 ? customStocks : null,
        risk_level: "medium",
        mode: mode
      });
      setAnalysis(response.data);
      setStep(5);
    } catch (error) {
      toast.error("Failed to analyze portfolio");
    } finally {
      setLoading(false);
    }
  };

  const createPortfolio = async () => {
    if (!portfolioName) {
      toast.error("Please enter a portfolio name");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/wizard/create-portfolio`, null, {
        params: {
          name: portfolioName,
          basket_id: selectedBasket?.id,
          investment_amount: investment
        }
      });
      toast.success("Portfolio created successfully!");
      navigate("/my-portfolios");
    } catch (error) {
      toast.error("Failed to create portfolio");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const totalSteps = 5;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Sparkles className="w-6 h-6 text-primary" />
            Portfolio Wizard
          </h1>
          <p className="text-sm text-muted-foreground">
            Build your first investment portfolio in minutes
          </p>
        </div>
        <Button variant="outline" onClick={() => setGuideOpen(true)} data-testid="open-guide-btn">
          <BookOpen className="w-4 h-4 mr-2" />
          Beginner's Guide
        </Button>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Step {step} of {totalSteps}</span>
          <span>{Math.round((step / totalSteps) * 100)}% complete</span>
        </div>
        <Progress value={(step / totalSteps) * 100} className="h-2" />
      </div>

      {/* Step 1: Choose Mode */}
      {step === 1 && (
        <Card data-testid="step-1">
          <CardHeader>
            <CardTitle className="text-lg">How would you like to build your portfolio?</CardTitle>
            <CardDescription>Choose the level of help you need</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card 
                className={`cursor-pointer transition-all ${mode === 'guided' ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'}`}
                onClick={() => setMode('guided')}
                data-testid="mode-guided"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-green-500" />
                  </div>
                  <h3 className="font-bold text-lg">🐣 Guided</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Answer simple questions, we pick the best portfolio for you
                  </p>
                  <Badge className="mt-3 bg-green-500">Best for beginners</Badge>
                </CardContent>
              </Card>

              <Card 
                className={`cursor-pointer transition-all ${mode === 'hybrid' ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'}`}
                onClick={() => setMode('hybrid')}
                data-testid="mode-hybrid"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Scale className="w-6 h-6 text-blue-500" />
                  </div>
                  <h3 className="font-bold text-lg">⚖️ Hybrid</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Start with a template, then add your own picks
                  </p>
                  <Badge className="mt-3 bg-blue-500">Some experience</Badge>
                </CardContent>
              </Card>

              <Card 
                className={`cursor-pointer transition-all ${mode === 'custom' ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'}`}
                onClick={() => setMode('custom')}
                data-testid="mode-custom"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Target className="w-6 h-6 text-purple-500" />
                  </div>
                  <h3 className="font-bold text-lg">🎯 Custom</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Build from scratch - pick your own funds and stocks
                  </p>
                  <Badge className="mt-3 bg-purple-500">Advanced</Badge>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={() => setStep(2)} disabled={!mode} data-testid="next-step-1">
                Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Investment Amount */}
      {step === 2 && (
        <Card data-testid="step-2">
          <CardHeader>
            <CardTitle className="text-lg">💷 How much would you like to invest?</CardTitle>
            <CardDescription>You can start with as little as £500</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center py-8">
              <p className="text-5xl font-bold text-primary" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {formatCurrency(investment)}
              </p>
            </div>

            <div className="space-y-4">
              <Slider
                value={[investment]}
                min={500}
                max={50000}
                step={100}
                onValueChange={(value) => setInvestment(value[0])}
                className="w-full"
                data-testid="investment-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>£500</span>
                <span>£10,000</span>
                <span>£25,000</span>
                <span>£50,000</span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {[1000, 2500, 5000, 10000].map((amount) => (
                <Button
                  key={amount}
                  variant={investment === amount ? "default" : "outline"}
                  size="sm"
                  onClick={() => setInvestment(amount)}
                >
                  {formatCurrency(amount)}
                </Button>
              ))}
            </div>

            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                💡 <strong>Tip:</strong> Start with what you can afford to lose. Never invest your emergency savings!
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(3)} data-testid="next-step-2">
                Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Risk Questions (Guided) or Fund Selection (Custom) */}
      {step === 3 && mode === 'guided' && (
        <Card data-testid="step-3-guided">
          <CardHeader>
            <CardTitle className="text-lg">🎯 Let's find your risk level</CardTitle>
            <CardDescription>Answer honestly - there are no wrong answers!</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {questions.map((q, qIndex) => (
              <div key={q.id} className="space-y-3">
                <p className="font-medium">{qIndex + 1}. {q.question}</p>
                <div className="grid grid-cols-2 gap-2">
                  {q.options.map((opt) => (
                    <Button
                      key={opt.value}
                      variant={answers[q.id] === opt.value ? "default" : "outline"}
                      className="h-auto py-3 justify-start"
                      onClick={() => setAnswers({ ...answers, [q.id]: opt.value })}
                    >
                      <span className="text-left">
                        <span className="block">{opt.label}</span>
                        <span className="text-xs text-muted-foreground">{opt.description}</span>
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                disabled={Object.keys(answers).length < questions.length}
                data-testid="next-step-3"
              >
                See Recommendations <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Custom Fund Selection */}
      {step === 3 && (mode === 'custom' || mode === 'hybrid') && (
        <Card data-testid="step-3-custom">
          <CardHeader>
            <CardTitle className="text-lg">🎯 Build Your Portfolio</CardTitle>
            <CardDescription>
              {mode === 'hybrid' ? 'Start with a basket, then customize' : 'Search and add funds to your portfolio'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basket Selection for Hybrid */}
            {mode === 'hybrid' && (
              <div className="space-y-3">
                <Label>Start with a basket (optional)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {baskets.map((basket) => (
                    <Button
                      key={basket.id}
                      variant={selectedBasket?.id === basket.id ? "default" : "outline"}
                      className="h-auto py-3"
                      onClick={() => setSelectedBasket(basket)}
                    >
                      <span>{BASKET_ICONS[basket.id]} {basket.name.replace(/[^\w\s]/g, '').trim()}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Fund Search */}
            <div className="space-y-3">
              <Label>Search for funds to add</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search funds (e.g., Vanguard, Technology)"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchFunds(e.target.value);
                    }}
                    className="pl-9"
                  />
                </div>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {searchResults.map((fund) => (
                    <div
                      key={fund.id}
                      className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                      onClick={() => addCustomFund(fund)}
                    >
                      <div>
                        <p className="font-medium text-sm">{fund.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {fund.sector} • OCF: {fund.ocf}% • Min: £{fund.min_investment}
                        </p>
                      </div>
                      <Plus className="w-4 h-4 text-primary" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Funds */}
            {customFunds.length > 0 && (
              <div className="space-y-3">
                <Label>Your selected funds ({customFunds.length})</Label>
                <div className="space-y-2">
                  {customFunds.map((fund) => (
                    <div key={fund.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{fund.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Allocation: {fund.allocation.toFixed(0)}% • £{(investment * fund.allocation / 100).toFixed(0)}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeCustomFund(fund.id)}>
                        <X className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => setStep(4)}
                disabled={mode === 'custom' && customFunds.length === 0}
                data-testid="next-step-3"
              >
                Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Basket Selection (Guided) or Review (Custom) */}
      {step === 4 && mode === 'guided' && (
        <Card data-testid="step-4-guided">
          <CardHeader>
            <CardTitle className="text-lg">🎁 Choose Your Portfolio</CardTitle>
            <CardDescription>Based on your answers, here are your best matches</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {baskets.filter(b => b.min_investment <= investment).map((basket) => (
                <Card
                  key={basket.id}
                  className={`cursor-pointer transition-all ${BASKET_COLORS[basket.color]} ${
                    selectedBasket?.id === basket.id ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => setSelectedBasket(basket)}
                  data-testid={`basket-${basket.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="text-center mb-4">
                      <span className="text-4xl">{BASKET_ICONS[basket.id]}</span>
                      <h3 className="font-bold text-lg mt-2">{basket.name.replace(/[^\w\s]/g, '').trim()}</h3>
                      <p className="text-sm text-muted-foreground">{basket.tagline}</p>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Risk Level</span>
                        <Badge variant="outline">{basket.risk_level}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expected Return</span>
                        <span className="font-medium">{basket.expected_return}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Time Horizon</span>
                        <span>{basket.time_horizon}</span>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-4 p-2 bg-background/50 rounded">
                      {basket.who_is_this_for}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={analyzePortfolio}
                disabled={!selectedBasket || loading}
                data-testid="analyze-portfolio"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Get AI Analysis <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review for Custom/Hybrid */}
      {step === 4 && (mode === 'custom' || mode === 'hybrid') && (
        <Card data-testid="step-4-custom">
          <CardHeader>
            <CardTitle className="text-lg">📋 Review Your Portfolio</CardTitle>
            <CardDescription>Let's get the AI agents to check your choices</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Total Investment</span>
                <span className="font-bold">{formatCurrency(investment)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Number of Funds</span>
                <span>{(selectedBasket?.funds?.length || 0) + customFunds.length}</span>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={analyzePortfolio}
                disabled={loading}
                data-testid="analyze-portfolio"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Get AI Analysis <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: AI Analysis & Final */}
      {step === 5 && analysis && (
        <Card data-testid="step-5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Your Portfolio is Ready!
            </CardTitle>
            <CardDescription>Here's what our AI agents think</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AI Agent Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Scout */}
              <Card className="border-green-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      🔭
                    </div>
                    <div>
                      <p className="font-bold text-green-400">The Scout</p>
                      <p className="text-xs text-muted-foreground">Growth Finder</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs">Growth Score:</span>
                    <Progress value={analysis.scout_analysis?.growth_score || 70} className="flex-1 h-2" />
                    <span className="text-xs font-bold text-green-400">{analysis.scout_analysis?.growth_score || 70}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    "{analysis.scout_analysis?.explanation || 'Looks promising!'}"
                  </p>
                </CardContent>
              </Card>

              {/* Skeptic */}
              <Card className="border-red-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      🛡️
                    </div>
                    <div>
                      <p className="font-bold text-red-400">The Skeptic</p>
                      <p className="text-xs text-muted-foreground">Risk Guardian</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs">Risk Level:</span>
                    <Badge className={
                      analysis.skeptic_analysis?.risk_level === 'HIGH' ? 'bg-red-500' :
                      analysis.skeptic_analysis?.risk_level === 'MEDIUM' ? 'bg-amber-500' :
                      'bg-green-500'
                    }>
                      {analysis.skeptic_analysis?.risk_level || 'MEDIUM'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    "{analysis.skeptic_analysis?.explanation || 'Be careful out there!'}"
                  </p>
                </CardContent>
              </Card>

              {/* Orchestrator */}
              <Card className="border-amber-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      ⚖️
                    </div>
                    <div>
                      <p className="font-bold text-amber-400">The Orchestrator</p>
                      <p className="text-xs text-muted-foreground">Final Decision</p>
                    </div>
                  </div>
                  <Badge className={
                    analysis.orchestrator_decision?.verdict === 'GO' ? 'bg-green-500 text-lg px-4 py-1' :
                    analysis.orchestrator_decision?.verdict === 'WAIT' ? 'bg-amber-500 text-lg px-4 py-1' :
                    'bg-red-500 text-lg px-4 py-1'
                  }>
                    {analysis.orchestrator_decision?.verdict || 'GO'}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    "{analysis.orchestrator_decision?.advice || 'Good to go!'}"
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Investment Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">📊 Your Investment Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis.investment_breakdown?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div>
                        <p className="font-medium text-sm">{item.fund_name}</p>
                        <p className="text-xs text-muted-foreground">HL Code: {item.hl_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(item.amount)}</p>
                        <p className="text-xs text-muted-foreground">{item.allocation_pct}%</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-primary/10 rounded flex justify-between">
                  <span>Annual Fees:</span>
                  <span className="font-bold">{formatCurrency(analysis.total_annual_fees || 0)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Create Portfolio */}
            <Card className="border-primary">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <Label>Name your portfolio</Label>
                    <Input
                      placeholder="e.g., My First Portfolio"
                      value={portfolioName}
                      onChange={(e) => setPortfolioName(e.target.value)}
                      data-testid="portfolio-name-input"
                    />
                  </div>
                  
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => window.open('https://www.hl.co.uk', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open HL Website
                    </Button>
                    <Button
                      className="flex-1 bg-gradient-to-r from-primary to-green-500"
                      onClick={createPortfolio}
                      disabled={loading || !portfolioName}
                      data-testid="create-portfolio-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Save to My Portfolios
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}

      {/* User Guide Dialog */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Beginner's Guide to Investing
            </DialogTitle>
            <DialogDescription>
              Everything explained in simple words
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue={guide[0]?.id} className="w-full">
            <TabsList className="flex flex-wrap h-auto gap-1">
              {guide.map((section) => (
                <TabsTrigger key={section.id} value={section.id} className="text-xs">
                  {section.emoji} {section.title.replace(/[^\w\s]/g, '').trim().split(' ').slice(0, 2).join(' ')}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {guide.map((section) => (
              <TabsContent key={section.id} value={section.id} className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line">
                      {section.content}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          <DialogFooter>
            <DialogClose asChild>
              <Button>Got it!</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioWizard;
